import { Alert, MenuItem, TextField } from '@mui/material'
import { ApplicationRecord } from 'shared/api/driverOnboarding'
import { Button } from 'shared/components/Button/Button'
import { TextV2 } from 'shared/components/Text/TextV2'
import { TYPOGRAPHY } from 'shared/theme/typographyV2'

import { isReviewSubmission } from './onboardingSubmission'
import { useOnboardingDraftState } from './useOnboardingDraftState'

export function DriverTaskForms({
  record,
  save,
  busy,
  driverView = false,
}: {
  record: ApplicationRecord
  save: (kind: string, data: unknown, submit?: boolean) => Promise<boolean>
  busy: boolean
  driverView?: boolean
}) {
  const equipment = record.steps.find((step) => step.kind === 'EQUIPMENT')
  const consent = record.steps.find((step) => step.kind === 'CONSENT')
  const [truck, setTruck] = useOnboardingDraftState<Record<string, any>>(
    { ...record.equipment, ...equipment?.draft },
    equipment?.draft
  )
  const [notes, setNotes] = useOnboardingDraftState((consent?.draft as any)?.notes || '', consent?.draft)
  const done = record.status === 'ELIGIBLE'
  const actions = (kind: string) => (
    <div className="flex flex-wrap gap-3">
      <Button type="submit" isDisabled={busy} name="intent" value="save">
        Save {kind === 'EQUIPMENT' ? 'truck info' : 'consent notes'}
      </Button>
      {driverView && (
        <Button variant="secondary" type="submit" isDisabled={busy} name="intent" value="review">
          Send for review
        </Button>
      )}
    </div>
  )
  return (
    <div className="space-y-5">
      {(!driverView || equipment?.assigned_at) && (
        <form
          className="space-y-4 rounded-xl border border-gray-200 bg-white p-5"
          onSubmit={(event) => {
            event.preventDefault()
            void save('EQUIPMENT', truck, isReviewSubmission(event))
          }}
        >
          <TextV2 type={TYPOGRAPHY.header3}>Truck info</TextV2>
          <TextV2>
            Enter what you know. Your admin will review this information and assign the truck to you.
          </TextV2>
          <fieldset
            disabled={busy || done || (driverView && !!equipment?.assignment_completed_at)}
            className="m-0 space-y-4 border-0 p-0"
          >
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-1">
              {Object.entries({
                vehicle_number: 'Truck unit number',
                vin_number: 'VIN',
                vehicle_make: 'Make',
                vehicle_model: 'Model',
                vehicle_year: 'Year',
              }).map(([key, label]) => (
                <TextField
                  key={key}
                  label={label}
                  value={truck[key] ?? ''}
                  type={key === 'vehicle_year' ? 'number' : 'text'}
                  onChange={(event) =>
                    setTruck({
                      ...truck,
                      [key]:
                        key === 'vehicle_year'
                          ? event.target.value
                            ? Number(event.target.value)
                            : null
                          : event.target.value,
                    })
                  }
                />
              ))}
            </div>
            <TextField
              select
              fullWidth
              label="Truck ownership"
              value={truck.ownership_type || ''}
              onChange={(event) => setTruck({ ...truck, ownership_type: event.target.value })}
            >
              <MenuItem value="">Not provided</MenuItem>
              <MenuItem value="OWN">Fleet owns equipment</MenuItem>
              <MenuItem value="LEASE">Fleet is leasing equipment</MenuItem>
              <MenuItem value="RENT">Fleet is renting equipment</MenuItem>
              <MenuItem value="LEASE_TO_OWN">Lease to own</MenuItem>
              <MenuItem value="FINANCED">Financed</MenuItem>
            </TextField>
            {!driverView && (
              <TextV2>
                To create a truck from this draft, complete the unit number, VIN, make, model, year, and
                ownership. You can also assign an existing truck during review.
              </TextV2>
            )}
            {!done && actions('EQUIPMENT')}
          </fieldset>
        </form>
      )}
      {(!driverView || consent?.assigned_at) && (
        <form
          className="space-y-4 rounded-xl border border-gray-200 bg-white p-5"
          onSubmit={(event) => {
            event.preventDefault()
            void save('CONSENT', { notes }, isReviewSubmission(event))
          }}
        >
          <TextV2 type={TYPOGRAPHY.header3}>Consent</TextV2>
          <Alert severity="info">
            You can save a question or note and return later. Saving or sending notes does not sign consent.
            The screening disclosure becomes available after your admin reviews the application.
          </Alert>
          <fieldset
            disabled={busy || done || (driverView && !!consent?.assignment_completed_at)}
            className="m-0 space-y-4 border-0 p-0"
          >
            <TextField
              label="Consent questions or notes (optional)"
              multiline
              fullWidth
              value={notes}
              inputProps={{ maxLength: 2000 }}
              onChange={(event) => setNotes(event.target.value)}
            />
            {!done && actions('CONSENT')}
          </fieldset>
          {driverView && !done && !consent?.assignment_completed_at && (
            <Button href={`/driver-screening-consent/${record.id}/`} variant="secondary">
              Open screening disclosure and consent
            </Button>
          )}
          {consent?.resolution === 'COMPLETED' && <TextV2>Signed consent is recorded.</TextV2>}
        </form>
      )}
    </div>
  )
}
